import React from 'react';
import { Button } from 'react-bootstrap';
import { useSelection } from '../context/SelectionContext';
import { useCart } from '../context/CartContext';

interface OrderSummaryProps {
  onAddedToCart?: () => void;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ onAddedToCart }) => {
  const { selectedItems, clearSelection } = useSelection();
  const { addToCart, priceCalculation } = useCart();

  if (selectedItems.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="mb-0">No items selected</p>
        <small className="text-muted">Please select vehicles in step 1</small>
      </div>
    );
  }

  const vatRate = priceCalculation?.vat?.rate ?? 0.21;
  const subtotal = selectedItems.reduce((sum, item) =>
    sum + (item.vehicle.price * item.quantity), 0
  );
  const tax = subtotal * vatRate;
  const total = subtotal + tax;

  const handleAddToCart = () => {
    // Add all selected items to cart
    selectedItems.forEach(item => {
      addToCart(item.vehicle, item.quantity);
    });
    
    // Clear selection
    clearSelection();
    
    // Notify parent component
    onAddedToCart?.();
  };

  const handleStartNewSelection = () => {
    clearSelection();
  };

  return (
    <div className="order-summary-clean mt-3">
      <h5>Ready to add to cart:</h5>
      <div id="order-summary" className="border p-3 mb-3">
        {selectedItems.map((item) => (
          <div key={item.vehicle.id} className="order-item py-2 border-bottom">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="fw-bold">{item.vehicle.name}</div>
                <div className="text-muted">
                  Quantity: {item.quantity} pieces
                </div>
                <div className="text-muted">
                  Price per piece: €{item.vehicle.price.toFixed(2)}
                </div>
              </div>
              <div className="text-end">
                <div className="fw-bold">€{(item.vehicle.price * item.quantity).toFixed(2)}</div>
              </div>
            </div>
          </div>
        ))}
        
        <div className="order-totals mt-3 pt-3 border-top">
          <div className="d-flex justify-content-between mb-1">
            <span>Subtotal:</span>
            <span>€{subtotal.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <span>VAT ({Math.round(vatRate * 100)}%):</span>
            <span>€{tax.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between fw-bold fs-5">
            <span>Total for this selection:</span>
            <span>€{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
      
      <div className="d-flex gap-2">
        <Button 
          variant="primary" 
          className="flex-fill"
          onClick={handleAddToCart}
        >
          Add to Shopping Cart
        </Button>
        <Button 
          variant="outline-secondary" 
          onClick={handleStartNewSelection}
        >
          Start New Selection
        </Button>
      </div>
    </div>
  );
};

export default OrderSummary;